import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { 
  BookOpen, Calendar, DollarSign, Tag, Building2, 
  Plus, Edit2, Trash2, X, Save, Search, Filter,
  ChevronDown, BarChart3, Users, Clock, Award,
  AlertCircle, CheckCircle2, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import AcademyCohortManager from './AcademyCohortManager';
const API = API_BASE;

// Sub-components
const CourseManager = ({ courses, specialisations, categories, providers, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    short_title: '',
    description: '',
    full_description: '',
    subject_area: '',
    specialisation: '',
    specialisation_id: '',
    level: 'certificate',
    qaa_level: 'UK QAA Level 2',
    provider: 'ICA',
    delivery_method: 'Flexible learning on the ICA LMS',
    cohort_dates: false,
    target_roles: [],
    duration: '3-6 months',
    assessment: 'Online assessment',
    awarding_body: 'ICA in association with Alliance Manchester Business School',
    entry_requirements: '',
    learning_outcomes: [],
    syllabus: [],
    career_outcomes: []
  });

  const resetForm = () => {
    setFormData({
      title: '',
      short_title: '',
      description: '',
      full_description: '',
      subject_area: '',
      specialisation: '',
      specialisation_id: '',
      level: 'certificate',
      qaa_level: 'UK QAA Level 2',
      provider: 'ICA',
      delivery_method: 'Flexible learning on the ICA LMS',
      cohort_dates: false,
      target_roles: [],
      duration: '3-6 months',
      assessment: 'Online assessment',
      awarding_body: 'ICA in association with Alliance Manchester Business School',
      entry_requirements: '',
      learning_outcomes: [],
      syllabus: [],
      career_outcomes: []
    });
    setEditingCourse(null);
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      ...course,
      target_roles: course.target_roles || [],
      learning_outcomes: course.learning_outcomes || [],
      syllabus: course.syllabus || [],
      career_outcomes: course.career_outcomes || []
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.provider || !formData.level) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingCourse) {
        await axios.put(`${API}/api/ica/admin/courses/${editingCourse.course_id}`, formData);
        toast.success('Course updated successfully');
      } else {
        await axios.post(`${API}/api/ica/admin/courses`, formData);
        toast.success('Course created successfully');
      }
      setShowForm(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Failed to save course:', error);
      toast.error(error.response?.data?.detail || 'Failed to save course');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;

    try {
      await axios.delete(`${API}/api/ica/admin/courses/${courseId}`);
      toast.success('Course deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Failed to delete course:', error);
      toast.error('Failed to delete course');
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = !searchTerm || 
      course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProvider = filterProvider === 'all' || course.provider === filterProvider;
    const matchesLevel = filterLevel === 'all' || course.level === filterLevel;
    return matchesSearch && matchesProvider && matchesLevel;
  });

  const levelOptions = [
    { id: 'short-course', name: 'Short Course', qaa: null },
    { id: 'certificate', name: 'Certificate', qaa: 'UK QAA Level 2' },
    { id: 'specialist', name: 'Specialist Certificate', qaa: 'UK QAA Level 2' },
    { id: 'advanced', name: 'Advanced Certificate', qaa: 'UK QAA Level 4' },
    { id: 'diploma', name: 'Diploma', qaa: 'UK QAA Level 6' },
    { id: 'postgrad', name: 'Postgraduate Diploma', qaa: 'UK QAA Level 8' },
    { id: 'practitioner', name: 'Certified Practitioner', qaa: null },
    { id: 'occupational', name: 'Occupational Certificate', qaa: 'NQF Level 5-8' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Course Management</h3>
          <p className="text-sm text-slate-400">{courses.length} courses in catalog</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900"
          data-testid="add-course-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Course
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <select
          value={filterProvider}
          onChange={(e) => setFilterProvider(e.target.value)}
          className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
        >
          <option value="all">All Providers</option>
          <option value="ICA">ICA</option>
          <option value="ICITP">ICITP</option>
        </select>
        <select
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
        >
          <option value="all">All Levels</option>
          {levelOptions.map(opt => (
            <option key={opt.id} value={opt.id}>{opt.name}</option>
          ))}
        </select>
      </div>

      {/* Course List */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-800">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Course</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Provider</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Level</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Specialisation</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Delivery</th>
              <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {filteredCourses.map(course => (
              <tr key={course.course_id} className="hover:bg-slate-800/50">
                <td className="p-4">
                  <div className="text-white font-medium">{course.title}</div>
                  <div className="text-xs text-slate-500">{course.course_id}</div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    course.provider === 'ICA' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {course.provider}
                  </span>
                </td>
                <td className="p-4 text-slate-300 text-sm">{course.level}</td>
                <td className="p-4 text-slate-300 text-sm">{course.specialisation || '-'}</td>
                <td className="p-4">
                  <span className={`text-xs ${course.cohort_dates ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {course.cohort_dates ? 'Cohort' : 'Anytime'}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(course)}
                      className="text-slate-400 hover:text-white"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(course.course_id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCourses.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            No courses found
          </div>
        )}
      </div>

      {/* Add/Edit Course Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
            <div className="p-6 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">
                  {editingCourse ? 'Edit Course' : 'Add New Course'}
                </h3>
                <button
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-slate-400" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-amber-400">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-sm text-slate-400 mb-1 block">Title *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Short Title</label>
                    <Input
                      value={formData.short_title}
                      onChange={(e) => setFormData(prev => ({ ...prev, short_title: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="e.g., Cert in AML"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Provider *</label>
                    <select
                      value={formData.provider}
                      onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value }))}
                      className="w-full h-10 px-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
                    >
                      <option value="ICA">ICA</option>
                      <option value="ICITP">ICITP</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Level *</label>
                    <select
                      value={formData.level}
                      onChange={(e) => {
                        const level = levelOptions.find(l => l.id === e.target.value);
                        setFormData(prev => ({ 
                          ...prev, 
                          level: e.target.value,
                          qaa_level: level?.qaa || prev.qaa_level,
                          cohort_dates: ['advanced', 'diploma', 'postgrad', 'practitioner', 'occupational'].includes(e.target.value)
                        }));
                      }}
                      className="w-full h-10 px-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
                    >
                      {levelOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">QAA Level</label>
                    <Input
                      value={formData.qaa_level || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, qaa_level: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="e.g., UK QAA Level 4"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Specialisation</label>
                    <select
                      value={formData.specialisation_id}
                      onChange={(e) => {
                        const spec = specialisations.find(s => s.id === e.target.value);
                        setFormData(prev => ({ 
                          ...prev, 
                          specialisation_id: e.target.value,
                          specialisation: spec?.name || '',
                          subject_area: spec?.name || prev.subject_area
                        }));
                      }}
                      className="w-full h-10 px-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
                    >
                      <option value="">Select specialisation...</option>
                      {specialisations.map(spec => (
                        <option key={spec.id} value={spec.id}>{spec.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Duration</label>
                    <Input
                      value={formData.duration}
                      onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="e.g., 3-6 months"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <input
                      type="checkbox"
                      id="cohort_dates"
                      checked={formData.cohort_dates}
                      onChange={(e) => setFormData(prev => ({ ...prev, cohort_dates: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                    />
                    <label htmlFor="cohort_dates" className="text-sm text-slate-300">
                      Cohort dates apply
                    </label>
                  </div>
                </div>
              </div>

              {/* Descriptions */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-amber-400">Descriptions</h4>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Short Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm resize-none"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Full Description</label>
                  <textarea
                    value={formData.full_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_description: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm resize-none"
                    rows={4}
                  />
                </div>
              </div>

              {/* Delivery */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-amber-400">Delivery & Assessment</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Delivery Method</label>
                    <Input
                      value={formData.delivery_method}
                      onChange={(e) => setFormData(prev => ({ ...prev, delivery_method: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Assessment</label>
                    <Input
                      value={formData.assessment}
                      onChange={(e) => setFormData(prev => ({ ...prev, assessment: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm text-slate-400 mb-1 block">Awarding Body</label>
                    <Input
                      value={formData.awarding_body}
                      onChange={(e) => setFormData(prev => ({ ...prev, awarding_body: e.target.value }))}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm text-slate-400 mb-1 block">Entry Requirements</label>
                    <textarea
                      value={formData.entry_requirements}
                      onChange={(e) => setFormData(prev => ({ ...prev, entry_requirements: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm resize-none"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Lists */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-amber-400">Learning Content</h4>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Learning Outcomes (one per line)</label>
                  <textarea
                    value={formData.learning_outcomes?.join('\n') || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      learning_outcomes: e.target.value.split('\n').filter(x => x.trim()) 
                    }))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm resize-none"
                    rows={4}
                    placeholder="Enter each learning outcome on a new line"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Syllabus Modules (one per line)</label>
                  <textarea
                    value={formData.syllabus?.join('\n') || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      syllabus: e.target.value.split('\n').filter(x => x.trim()) 
                    }))}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm resize-none"
                    rows={4}
                    placeholder="Enter each module on a new line"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Career Outcomes (comma separated)</label>
                  <Input
                    value={formData.career_outcomes?.join(', ') || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      career_outcomes: e.target.value.split(',').map(x => x.trim()).filter(x => x) 
                    }))}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="e.g., AML Analyst, Compliance Officer"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="flex-1 border-slate-700 text-white"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingCourse ? 'Update Course' : 'Create Course'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const PricingManager = ({ pricing, exchangeRate, onRefresh }) => {
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState({});

  const handleEdit = (tierId, tier) => {
    setEditingTier(tierId);
    setFormData({ ...tier });
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/api/ica/admin/pricing/${editingTier}`, formData);
      toast.success('Pricing updated successfully');
      setEditingTier(null);
      onRefresh();
    } catch (error) {
      console.error('Failed to update pricing:', error);
      toast.error('Failed to update pricing');
    }
  };

  const tierLabels = {
    'short-course': 'Short Course',
    'certificate': 'Certificate',
    'specialist': 'Specialist Certificate',
    'advanced': 'Advanced Certificate',
    'diploma': 'Diploma',
    'practitioner': 'Certified Practitioner',
    'postgrad': 'Postgraduate Diploma',
    'occupational': 'Occupational Certificate',
    'essential': 'Essential'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Pricing Management</h3>
          <p className="text-sm text-slate-400">
            Exchange rate: £1 = R{exchangeRate?.gbp_zar?.toFixed(2) || '23.50'}
          </p>
        </div>
      </div>

      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-800">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Tier</th>
              <th className="text-right p-4 text-sm font-medium text-slate-400">ICA Price (GBP)</th>
              <th className="text-right p-4 text-sm font-medium text-slate-400">Discount %</th>
              <th className="text-right p-4 text-sm font-medium text-slate-400">Navigate Price (GBP)</th>
              <th className="text-right p-4 text-sm font-medium text-slate-400">Navigate Price (ZAR)</th>
              <th className="text-right p-4 text-sm font-medium text-slate-400">Savings (ZAR)</th>
              <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {Object.entries(pricing || {}).map(([tierId, tier]) => (
              <tr key={tierId} className="hover:bg-slate-800/50">
                {editingTier === tierId ? (
                  <>
                    <td className="p-4 text-white font-medium">{tierLabels[tierId] || tierId}</td>
                    <td className="p-4">
                      <Input
                        type="number"
                        value={formData.ica_price_gbp || 0}
                        onChange={(e) => setFormData(prev => ({ ...prev, ica_price_gbp: parseFloat(e.target.value) }))}
                        className="w-24 bg-slate-700 border-slate-600 text-white text-right"
                      />
                    </td>
                    <td className="p-4">
                      <Input
                        type="number"
                        value={formData.discount_percent || 0}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) }))}
                        className="w-20 bg-slate-700 border-slate-600 text-white text-right"
                      />
                    </td>
                    <td className="p-4 text-right text-slate-300">
                      £{Math.round(formData.ica_price_gbp * (1 - (formData.discount_percent || 0) / 100))}
                    </td>
                    <td className="p-4 text-right text-slate-300">
                      R{Math.round(formData.ica_price_gbp * (1 - (formData.discount_percent || 0) / 100) * (exchangeRate?.gbp_zar || 23.5)).toLocaleString()}
                    </td>
                    <td className="p-4 text-right text-emerald-400">-</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-400">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingTier(null)} className="text-slate-400">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-4 text-white font-medium">{tierLabels[tierId] || tierId}</td>
                    <td className="p-4 text-right text-slate-300">£{tier.ica_price_gbp?.toLocaleString()}</td>
                    <td className="p-4 text-right text-amber-400">{tier.discount_percent}%</td>
                    <td className="p-4 text-right text-white font-medium">£{tier.navigate_price_gbp?.toLocaleString()}</td>
                    <td className="p-4 text-right text-white font-medium">R{tier.navigate_price_zar?.toLocaleString()}</td>
                    <td className="p-4 text-right text-emerald-400">R{tier.savings_zar?.toLocaleString()}</td>
                    <td className="p-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(tierId, tier)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StatsCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-slate-400">{title}</span>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
  </div>
);

// Main Component
export default function AcademyAdminManager() {
  const [activeTab, setActiveTab] = useState('courses');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [pricing, setPricing] = useState({});
  const [exchangeRate, setExchangeRate] = useState(null);
  const [specialisations, setSpecialisations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesRes, pricingRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/ica/admin/courses`),
        axios.get(`${API}/api/ica/admin/pricing`),
        axios.get(`${API}/api/ica/admin/stats`)
      ]);

      setCourses(coursesRes.data.courses || []);
      setSpecialisations(coursesRes.data.specialisations || []);
      setCategories(coursesRes.data.categories || []);
      setProviders(coursesRes.data.providers || {});
      setPricing(pricingRes.data.pricing_tiers || {});
      setExchangeRate(pricingRes.data.exchange_rate);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch Academy data:', error);
      toast.error('Failed to load Academy data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'cohorts', label: 'Intake Dates', icon: Calendar },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Navigate Academy Management</h2>
        <p className="text-slate-400 text-sm">Manage courses, pricing, and intake dates</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          title="Total Courses"
          value={stats?.courses?.total || 0}
          subtitle={`ICA: ${stats?.courses?.by_provider?.ICA || 0} | ICITP: ${stats?.courses?.by_provider?.ICITP || 0}`}
          icon={BookOpen}
          color="bg-blue-500/20"
        />
        <StatsCard
          title="Pending Inquiries"
          value={stats?.inquiries?.pending || 0}
          subtitle={`Total: ${stats?.inquiries?.total || 0}`}
          icon={Users}
          color="bg-amber-500/20"
        />
        <StatsCard
          title="Active Cohorts"
          value={stats?.cohorts?.total || 0}
          subtitle={`Upcoming: ${stats?.cohorts?.upcoming || 0}`}
          icon={Calendar}
          color="bg-emerald-500/20"
        />
        <StatsCard
          title="Exchange Rate"
          value={`R${exchangeRate?.gbp_zar?.toFixed(2) || '23.50'}`}
          subtitle="GBP to ZAR"
          icon={DollarSign}
          color="bg-purple-500/20"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <div className="flex gap-1">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'text-amber-400 border-amber-400'
                    : 'text-slate-400 border-transparent hover:text-white'
                }`}
                data-testid={`academy-tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'courses' && (
          <CourseManager
            courses={courses}
            specialisations={specialisations}
            categories={categories}
            providers={providers}
            onRefresh={fetchData}
          />
        )}
        {activeTab === 'cohorts' && <AcademyCohortManager />}
        {activeTab === 'pricing' && (
          <PricingManager
            pricing={pricing}
            exchangeRate={exchangeRate}
            onRefresh={fetchData}
          />
        )}
      </div>
    </div>
  );
}
